import { rd } from '@passionware/monads';
import type { Resource } from '__types__';
import { useTranslation } from 'react-i18next';
import { WithServices } from 'v5/platform/ts/services';
import { WithToastService } from 'v5/services/front/ToastService/ToastService';
import type { WithUserPreferencesService } from 'v5/services/front/UserPreferencesService/UserPreferencesService';
import { WithUploadService } from 'v5/services/io/UploadService/UploadService';
import { FilesPanel } from 'v5/patterns/widgets/attachments/FilesPanel';
import {
  useResourceAssociationActions,
  useResourceAssociations
} from './useResourceAssociations';

export interface ResourceFilesPanelProps
  extends WithServices<
    [WithUploadService, WithToastService, WithUserPreferencesService]
  > {
  resource: Pick<Resource, 'id' | 'first_name'>;
  disabled: boolean;
}

export function ResourceFilesPanel({
  services,
  resource,
  disabled
}: ResourceFilesPanelProps) {
  const { t } = useTranslation('resources');
  const actions = useResourceAssociationActions(resource.id);
  const associations = useResourceAssociations(resource.id);

  return (
    <FilesPanel
      services={services}
      documents={rd.map(associations, ({ documents = [] }) => documents)}
      onCreate={actions.createDocument}
      onDelete={actions.deleteDocument}
      onReorder={actions.reorderDocuments}
      disabled={disabled}
      copy={{
        title: t('ResourceFilesPanel.title'),
        emptyTitle: t('ResourceFilesPanel.emptyTitle'),
        emptyDescription: t('ResourceFilesPanel.emptyDescription', {
          name: resource.first_name || t('ResourceFilesPanel.fallbackName')
        }),
        loadError: t('ResourceFilesPanel.loadError'),
        untitled: t('ResourceFilesPanel.untitled'),
        uploadedToast: t('ResourceFilesPanel.uploadedToast'),
        uploadErrorToast: t('ResourceFilesPanel.uploadErrorToast'),
        reorderErrorToast: t('ResourceFilesPanel.reorderErrorToast')
      }}
    />
  );
}
